import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import type {
  Chemical,
  ChemicalLabel,
  ChemicalFilters,
  PaginatedChemicalsResponse,
  ChemicalCreateRequest,
  ChemicalUpdateRequest,
  ChemicalImportResponse,
  ChemicalImportRow,
  ChemicalImportJobResponse,
  LogoUploadUrlResponse
} from '../models/chemical.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PaginatedApiResponse {
  success: boolean;
  data?: {
    chemicals: Chemical[];
    total: number;
    page: number;
    limit: number;
  };
  error?: string;
}

/**
 * Chemicals Service (Admin)
 *
 * Service for managing ADN Recommended Chemicals in the admin portal.
 * Communicates with /admin/chemicals endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class ChemicalsService {
  private readonly apiUrl = `${environment.apiUrl}/admin/chemicals`;
  private readonly fileUploadsUrl = `${environment.apiUrl}/admin/file-uploads`;

  constructor(private http: HttpClient) {}

  /**
   * List chemicals with optional filters and pagination
   *
   * @param filters - Optional filter parameters
   * @returns Observable with paginated chemicals response
   */
  listChemicals(filters?: ChemicalFilters): Observable<PaginatedChemicalsResponse> {
    const params = new URLSearchParams();

    if (filters?.page !== undefined && filters.page !== null) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit !== undefined && filters.limit !== null) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.query) {
      params.append('query', filters.query);
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.signalWord) {
      params.append('signalWord', filters.signalWord);
    }
    if (filters?.featured !== undefined && filters.featured !== null) {
      params.append('featured', filters.featured.toString());
    }
    if (filters?.sponsor !== undefined && filters.sponsor !== null) {
      params.append('sponsor', filters.sponsor.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

    return this.http.get<PaginatedApiResponse>(url).pipe(
      map((response) => {
        if (response.success && response.data) {
          return {
            chemicals: response.data.chemicals,
            total: response.data.total,
            page: response.data.page,
            pageSize: response.data.limit
          };
        }
        return { chemicals: [], total: 0, page: 1, pageSize: 20 };
      })
    );
  }

  /**
   * Get a single chemical by ID
   *
   * @param id - Chemical ID
   * @returns Observable with the chemical or null if not found
   */
  getChemical(id: string): Observable<Chemical | null> {
    return this.http.get<ApiResponse<Chemical>>(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      })
    );
  }

  /**
   * Create a new chemical
   *
   * @param data - Chemical data to create
   * @returns Observable with the created chemical
   */
  createChemical(data: ChemicalCreateRequest): Observable<Chemical> {
    return this.http.post<ApiResponse<Chemical>>(this.apiUrl, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create chemical');
      })
    );
  }

  /**
   * Update an existing chemical
   *
   * @param id - Chemical ID
   * @param data - Partial chemical data to update
   * @returns Observable with the updated chemical
   */
  updateChemical(id: string, data: ChemicalUpdateRequest): Observable<Chemical> {
    return this.http.put<ApiResponse<Chemical>>(`${this.apiUrl}/${id}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update chemical');
      })
    );
  }

  /**
   * Archive a chemical (soft delete)
   *
   * @param id - Chemical ID
   * @returns Observable that completes on success
   */
  archiveChemical(id: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to archive chemical');
        }
      })
    );
  }

  /**
   * Download the CSV import template
   *
   * @returns Observable with the template file as a Blob
   */
  getImportTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/import-template`, {
      responseType: 'blob'
    });
  }

  /**
   * Import chemicals from parsed CSV data (async via background job)
   *
   * This method queues the import for background processing and returns immediately.
   * Subscribe to WebSocket events for progress updates:
   * - 'chemical-import:progress' - Progress during import
   * - 'chemical-import:complete' - Import completed successfully
   * - 'chemical-import:failed' - Import failed
   *
   * @param rows - Array of parsed CSV row objects
   * @param options - Import options
   * @param options.enrichFromEpa - If true, run EPA enrichment after import
   * @returns Observable with job info (jobId, status, message)
   */
  importChemicals(rows: ChemicalImportRow[], options?: { enrichFromEpa?: boolean }): Observable<ChemicalImportJobResponse> {
    const body = {
      rows,
      enrichFromEpa: options?.enrichFromEpa ?? true  // Default to true for EPA enrichment
    };
    return this.http.post<ApiResponse<ChemicalImportJobResponse>>(`${this.apiUrl}/import`, body).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to queue import');
      })
    );
  }

  /**
   * Get import job status
   *
   * @param jobId - The job ID returned from importChemicals
   * @returns Observable with job status info
   */
  getImportJobStatus(jobId: string): Observable<any> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/import/${jobId}/status`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to get job status');
      })
    );
  }

  /**
   * Get a pre-signed URL for uploading a sponsor logo
   *
   * @param filename - Original filename of the logo
   * @returns Observable with upload URL and final file URL
   */
  getLogoUploadUrl(filename: string): Observable<LogoUploadUrlResponse> {
    return this.http
      .post<ApiResponse<LogoUploadUrlResponse>>(`${this.fileUploadsUrl}/chemical-logo`, { filename })
      .pipe(
        map((response) => {
          if (response.success && response.data) {
            return response.data;
          }
          throw new Error(response.error || 'Failed to get upload URL');
        })
      );
  }

  // ============================================
  // LABEL MANAGEMENT
  // ============================================

  /**
   * Add a label to a chemical
   */
  addLabel(chemicalId: string, data: {
    labelUrl: string;
    labelName?: string;
    acceptedDate?: string;
    setAsCurrent?: boolean;
  }): Observable<ChemicalLabel> {
    return this.http.post<ApiResponse<ChemicalLabel>>(
      `${this.apiUrl}/${chemicalId}/labels`,
      data
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to add label');
      })
    );
  }

  /**
   * Delete a label from a chemical
   */
  deleteLabel(chemicalId: string, labelId: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/${chemicalId}/labels/${labelId}`
    ).pipe(
      map((response) => {
        if (response && !response.success) {
          throw new Error(response.error || 'Failed to delete label');
        }
      })
    );
  }

  /**
   * Toggle a label's current status
   * Returns the toggled label and all labels for the chemical
   */
  setCurrentLabel(chemicalId: string, labelId: string): Observable<{ label: ChemicalLabel; labels: ChemicalLabel[] }> {
    return this.http.put<ApiResponse<{ label: ChemicalLabel; labels: ChemicalLabel[] }>>(
      `${this.apiUrl}/${chemicalId}/labels/${labelId}/set-current`,
      {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to set current label');
      })
    );
  }
}
